import {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {getFromApi, postToApi, patchToApi, deleteFromApi} from "../api/auth";
import InlineRename from "../components/shared/InlineRename";
import ConfirmButton from "../components/shared/ConfirmButton";

export default function ProjectLog() {
    const [projects, setProjects] = useState([]);
    const [name, setName]         = useState("");
    const [parentId, setParentId] = useState("");
    const [filter, setFilter]     = useState("");
    const [loading, setLoading]   = useState(true);

    const load = async () => {
        try {
            const res = await getFromApi("api/projects");
            setProjects(res.data || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const handleAdd = async (e) => {
        e.preventDefault();
        await postToApi("api/projects", {name, parent_id: parentId || undefined});
        setName(""); setParentId(""); load();
    };

    const handleRename = async (id, newName) => {
        await patchToApi(`api/projects/${id}`, {name: newName});
        load();
    };

    const handleDelete = async (id) => {
        await deleteFromApi(`api/projects/${id}`);
        load();
    };

    const formatActivity = (iso) => {
        if (!iso) return null;
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d) / 86400000);
        if (diff === 0) return "Active today";
        if (diff === 1) return "Active yesterday";
        return `Active ${diff}d ago`;
    };

    const roots    = projects.filter(p => !p.parent_id);
    const displayed = filter
        ? projects.filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
        : roots;

    return (
        <div>
            <div className="page-header">
                <h4><i className="bi bi-kanban me-2 text-warning"/>Project Log</h4>
            </div>

            <div className="panel mb-4">
                <form onSubmit={handleAdd} className="row g-2 align-items-end">
                    <div className="col-md-5">
                        <label className="form-label small fw-medium">Project name</label>
                        <input className="form-control" placeholder="Project name..." value={name} onChange={e => setName(e.target.value)} required/>
                    </div>
                    <div className="col-md-4">
                        <label className="form-label small fw-medium">Parent (optional)</label>
                        <select className="form-select" value={parentId} onChange={e => setParentId(e.target.value)}>
                            <option value="">Root project</option>
                            {roots.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <button type="submit" className="btn btn-dark w-100"><i className="bi bi-plus-lg me-1"/>Add</button>
                    </div>
                </form>
            </div>

            <div className="mb-3">
                <input className="form-control" placeholder="Filter projects..." value={filter} onChange={e => setFilter(e.target.value)}/>
            </div>

            {loading ? <div className="text-muted text-center py-4">Loading...</div> : (
                <div className="panel p-0" style={{overflow:"hidden"}}>
                    {displayed.map((p, i) => {
                        const subs   = filter ? [] : projects.filter(s => String(s.parent_id) === String(p._id));
                        const isLast = i === displayed.length - 1;
                        const activity = formatActivity(p.lastActivity);
                        return (
                            <div key={p._id}>
                                <div className={`d-flex align-items-center justify-content-between px-4 py-3 ${!isLast||subs.length?"border-bottom":""}`}>
                                    <div>
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-folder2 text-warning"/>
                                            <InlineRename value={p.name} onSave={name => handleRename(p._id, name)} className="fw-medium"/>
                                            {subs.length > 0 && <span className="badge text-bg-light text-muted">{subs.length} sub</span>}
                                        </div>
                                        <div className="small text-muted mt-1">
                                            {p.entryCount || 0} {p.entryCount === 1 ? "entry" : "entries"}
                                            {activity && <span className="ms-2">· {activity}</span>}
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2 align-items-center">
                                        <Link to={`/project-log/${p._id}`} className="btn btn-sm btn-outline-secondary">Open</Link>
                                        <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => handleDelete(p._id)}>
                                            <i className="bi bi-trash"/>
                                        </ConfirmButton>
                                    </div>
                                </div>
                                {subs.map((s, si) => (
                                    <div key={s._id} className={`d-flex align-items-center justify-content-between px-4 py-2 bg-light ${si<subs.length-1||!isLast?"border-bottom":""}`}>
                                        <div className="d-flex align-items-center gap-2 ps-4">
                                            <i className="bi bi-folder text-secondary"/>
                                            <InlineRename value={s.name} onSave={name => handleRename(s._id, name)} className="text-secondary small"/>
                                            <span className="small text-muted">{s.entryCount || 0} entries</span>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Link to={`/project-log/${s._id}`} className="btn btn-sm btn-outline-secondary">Open</Link>
                                            <ConfirmButton className="btn btn-sm btn-outline-danger" onConfirm={() => handleDelete(s._id)}>
                                                <i className="bi bi-trash"/>
                                            </ConfirmButton>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                    {!displayed.length && (
                        <div className="empty-state">
                            <i className="bi bi-kanban"/>
                            <div>{filter ? "No projects match." : "No projects yet."}</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
